import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { SEO } from '../shared/components/SEO';
import { siteUrl } from '../shared/seo/siteOrigin';
import { fadeTransitionVariants } from '../shared/utils/microAnimations';

interface NotFoundPageProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'privacy-policy' | 'cookie-policy' | 'terms-of-service' | 'gdpr-compliance' | 'refund-cancellation' | 'faqs' | 'about-us' | 'blog' | 'contact-us') => void;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const currentPath = window.location.pathname;

  return (
    <motion.div
      className="min-h-screen vscode-bg-primary flex flex-col"
      variants={fadeTransitionVariants}
      initial="initial"
      animate="in"
      exit="out"
    >
      <SEO
        title="Page Not Found — GoGrabJob"
        description="This page doesn't exist. Head back to GoGrabJob to discover matched roles or browse our guides."
        canonical={siteUrl(typeof window !== 'undefined' ? window.location.pathname : '/')}
        noIndex
      />

      <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />

      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className={`flex-1 flex items-center justify-center px-4 py-12 relative z-10 ${MARKETING_NAV_TOP_OFFSET}`}>
        <div className="w-full max-w-2xl mx-auto text-center">
          {/* Error Icon */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-24 h-24 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full flex items-center justify-center border-2 border-cyan-500/30">
              <AlertTriangle className="w-12 h-12 text-cyan-400" />
            </div>
          </motion.div>

          {/* 404 Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-6 md:mb-8 px-2"
          >
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 mb-3 md:mb-4">
              404
            </h1>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-3 md:mb-4">
              {t('views.notFound.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-300 mb-2 leading-relaxed">
              {t('views.notFound.description')}
            </p>
            <p className="text-xs sm:text-sm text-gray-400 break-all">
              {t('views.notFound.requestedUrl')}: <code className="bg-gray-800 px-1 sm:px-2 py-1 rounded text-cyan-400 text-xs">{currentPath}</code>
            </p>
          </motion.div>

          {/* Suggested Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="px-2"
          >
            {/* Primary Action */}
            <div className="flex justify-center">
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 border border-gray-600 text-gray-300 font-semibold rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('views.notFound.goBack')}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Footer */}
      <MarketingPageFooter onNavigate={onNavigate} theme="home" />
    </motion.div>
  );
};

export default NotFoundPage;